import { create } from 'zustand';
import type { TaskPhase } from '@/services/tasking/sseArchiveService';

export interface ConvertionToCOGTask {
  taskId: string;
  status: 'idle' | 'processing' | 'completed' | 'failed';
  progress: number;
  overallProgress?: number;
  message: string;
  phase?: TaskPhase;
  url?: string;
  customLabel?: string;
  requestedStartDate?: string;
  requestedEndDate?: string;
  sourceMetadata?: {
    provider?: string;
    platform?: string;
    datetime?: string;
    cloudCover?: number;
    assetKey?: string;
  };
  result?: ConvertToCOGResult;
  error?: string;
  timestamp?: number;
}

export interface ConvertToCOGResult {
  status: 'success' | 'failed';
  original_url: string;
  s3_url: string;
  metadata: {
    provider: string;
    platform: string;
    datetime?: string;
    cloudCover?: number;
  };
}

interface TaskArchiveStore {
  tasks: Record<string, ConvertionToCOGTask>;
  tasksArray: ConvertionToCOGTask[];
  addTask: (taskId: string, url: string) => void;
  updateTask: (taskId: string, update: Partial<ConvertionToCOGTask>) => void;
  removeTask: (taskId: string) => void;
  getTask: (taskId: string) => ConvertionToCOGTask | undefined;
  clearCompletedTasks: () => void;
}

const tasksToArray = (tasks: Record<string, ConvertionToCOGTask>): ConvertionToCOGTask[] => {
  return Object.values(tasks);
};

export const useTaskArchiveStore = create<TaskArchiveStore>((set, get) => ({
  tasks: {},
  tasksArray: [],

  addTask: (taskId: string, url: string) => {
    set((state) => ({
      tasks: {
        ...state.tasks,
        [taskId]: {
          taskId,
          status: 'idle' as const,
          progress: 0,
          message: 'Starting...',
          url,
          timestamp: Date.now(),
        },
      },
      tasksArray: tasksToArray({
        ...state.tasks,
        [taskId]: {
          taskId,
          status: 'idle' as const,
          progress: 0,
          message: 'Starting...',
          url,
          timestamp: Date.now(),
        },
      }),
    }));
  },

  updateTask: (taskId: string, update: Partial<ConvertionToCOGTask>) => {
    set((state) => {
      const currentTask = state.tasks[taskId];
      if (!currentTask) return state;

      const updatedTask = { ...currentTask, ...update };

      if (
        update.progress !== undefined &&
        currentTask.progress !== undefined &&
        update.progress < currentTask.progress &&
        currentTask.status !== 'completed'
      ) {
        updatedTask.progress = currentTask.progress;
      }

      if (
        update.overallProgress !== undefined &&
        currentTask.overallProgress !== undefined &&
        update.overallProgress < currentTask.overallProgress &&
        currentTask.status !== 'completed'
      ) {
        updatedTask.overallProgress = currentTask.overallProgress;
      }

      updatedTask.timestamp = update.timestamp || Date.now();

      const newTasks = {
        ...state.tasks,
        [taskId]: updatedTask,
      };

      return {
        tasks: newTasks,
        tasksArray: tasksToArray(newTasks),
      };
    });
  },

  removeTask: (taskId: string) => {
    set((state) => {
      const { [taskId]: _, ...rest } = state.tasks;
      return {
        tasks: rest,
        tasksArray: tasksToArray(rest),
      };
    });
  },

  getTask: (taskId: string) => {
    return get().tasks[taskId];
  },

  clearCompletedTasks: () => {
    set((state) => {
      const tasks = { ...state.tasks };
      for (const taskId in tasks) {
        if (tasks[taskId].status === 'completed' || tasks[taskId].status === 'failed') {
          delete tasks[taskId];
        }
      }
      return {
        tasks,
        tasksArray: tasksToArray(tasks),
      };
    });
  },
}));
